import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { getConfig } from '../config';
import {
  beginLogin,
  beginPasswordReset,
  clearTokens,
  decodeIdToken,
  logout as hostedLogout,
  readTokens,
  refresh,
  storeTokens,
  type Tokens,
} from './oidc';
import { setTokenProvider } from '../api/client';

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
  roles: string[];
  isAdmin: boolean;
}

interface AuthContextValue {
  user?: AuthUser;
  ready: boolean;
  signIn: (returnTo?: string) => void;
  resetPassword: () => void;
  signOut: () => void;
  onTokens: (tokens: Tokens) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Refresh this long before expiry so an in-flight request never races the clock. */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<Tokens | undefined>(() => readTokens());
  const [ready, setReady] = useState(false);

  /**
   * Single source of truth for the bearer token. Everything that calls the API
   * goes through here, so refresh logic lives in exactly one place.
   */
  const getToken = useCallback(async (): Promise<string | undefined> => {
    const current = readTokens();
    if (!current) return undefined;
    if (current.expiresAt - Date.now() > REFRESH_MARGIN_MS) return current.idToken;
    if (!current.refreshToken) {
      clearTokens();
      setTokens(undefined);
      return undefined;
    }
    try {
      const renewed = await refresh(getConfig(), current.refreshToken);
      setTokens(renewed);
      return renewed.idToken;
    } catch {
      // The refresh token has been revoked or has expired: force a fresh login
      // rather than letting every request fail with a 401.
      clearTokens();
      setTokens(undefined);
      return undefined;
    }
  }, []);

  useEffect(() => {
    setTokenProvider(getToken);
    setReady(true);
  }, [getToken]);

  const user = useMemo<AuthUser | undefined>(() => {
    if (!tokens) return undefined;
    const claims = decodeIdToken(tokens.idToken);
    if (!claims) return undefined;
    const roles = claims['cognito:groups'] ?? [];
    return {
      sub: claims.sub,
      email: claims.email ?? '',
      name: claims.name ?? claims.email ?? claims.sub,
      roles,
      isAdmin: roles.includes('admin'),
    };
  }, [tokens]);

  /**
   * These are deliberately stable across renders.
   *
   * `ready` flips false→true in an effect, and when these were rebuilt with the
   * context value every consumer depending on one re-ran its effect. On the
   * callback screen that meant exchanging the authorization code twice — and
   * because the PKCE state and verifier are consumed on the first attempt, the
   * second failed with "Sign-in state mismatch" even though sign-in had
   * actually succeeded.
   */
  const signIn = useCallback((returnTo = window.location.pathname + window.location.search) => {
    void beginLogin(getConfig(), returnTo);
  }, []);

  const resetPassword = useCallback(() => {
    void beginPasswordReset(getConfig(), '/brands');
  }, []);

  const signOut = useCallback(() => {
    setTokens(undefined);
    hostedLogout(getConfig());
  }, []);

  const onTokens = useCallback((next: Tokens) => {
    storeTokens(next);
    setTokens(next);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, ready, signIn, resetPassword, signOut, onTokens }),
    [user, ready, signIn, resetPassword, signOut, onTokens],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
